import type { Maybe } from '@adornis/base/utilTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime } from 'luxon';
import { Workshop } from './Workshop.js';
import { WorkshopTrainingAttendance } from './WorkshopTrainingAttendance.js';

/**
 * @entity Workshop
 */
@Entity()
export class WorkshopAppointment extends MongoEntity {
  public static override _class = 'WorkshopAppointment';
  public static override _collectionName = 'workshop-appointments';

  @DataLoadedField(() => Workshop, ({ workshopID }) => workshopID)
  public workshop: Maybe<Workshop>;

  @validate(nonOptional())
  @Field(() => String)
  public workshopID: Maybe<string>;

  @Field(() => DateTime)
  public start: Maybe<DateTime>;

  @Field(() => DateTime)
  public end: Maybe<DateTime>;

  @Field(() => [WorkshopTrainingAttendance], {
    resolve: function (this: WorkshopAppointment) {
      return async () => {
        const appointment = (
          await loadMongoData(WorkshopAppointment, { _id: this._id }, { projection: { workshopID: 1, start: 1 } })
        )[0] as Maybe<EntityData<WorkshopAppointment>>;

        if (!appointment || !appointment.start) {
          return [];
        }

        const col = await getRawCollection<EntityData<WorkshopTrainingAttendance>>(
          WorkshopTrainingAttendance._collectionName,
        );
        return (await col
          .find(
            {
              workshopID: appointment.workshopID ?? '',
              $and: [
                {
                  date: {
                    $gte: DateTime.fromJSDate(appointment.start ?? new Date())
                      ?.startOf('day')
                      .toJSDate(),
                  },
                },
                {
                  date: {
                    $lte: DateTime.fromJSDate(appointment.start ?? new Date())
                      ?.endOf('day')
                      .toJSDate(),
                  },
                },
              ],
            },
            { projection: { accessToken: 0 } },
          )
          .toArray()) as WorkshopTrainingAttendance[];
      };
    },
  })
  public attendance: Maybe<WorkshopTrainingAttendance[]>;

  static async checkDuplicate(entity: WorkshopAppointment) {
    if (entity.start) {
      const res = await loadMongoData(WorkshopAppointment, {
        _id: entity._id ? { $ne: entity._id } : undefined,
        workshopID: entity.workshopID,
        start: { $gte: entity.start?.startOf('day').toJSDate(), $lte: entity.start?.endOf('day').toJSDate() },
      });

      if (res.length > 0) {
        throw new Error('Ein Termin für diesen Workshop existiert bereits an diesem Tag');
      }
    }
  }

  static override async beforeCreate(entity: WorkshopAppointment): Promise<void> {
    await this.checkDuplicate(entity);
    await super.beforeCreate(entity);
  }

  static override async beforeUpdate(
    oldEntity: { _id?: string | undefined; _class?: string | undefined },
    update: WorkshopAppointment,
  ): Promise<void> {
    await this.checkDuplicate(update);

    if (!update.start) {
      throw new Error('Startzeit muss angegeben werden');
    }

    const old = await WorkshopAppointment.getByID<WorkshopAppointment>(update._id)({
      start: 1,
      attendance: { date: 1 },
    });

    // Migrating attendance to new date
    if (old?.start?.startOf('day') !== update.start?.startOf('day')) {
      for (const a of old?.attendance ?? []) {
        a.date = update.start?.startOf('day');
        await a.save();
      }
    }

    await super.beforeUpdate(oldEntity, update);
  }
}
